import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { getEffectiveCompanyId } from '../utils/userCompany';
import { PRICING_PLANS, DEFAULT_PLAN_ID, PROFESSIONAL_TRIAL_DAYS } from '../config/pricingPlans';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import './Onboarding.css';
import '../components/BillingSection.css';

const OnboardingPaymentForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation('onboarding');
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });

      if (error) {
        setFormError(error.message || t('paymentSetupFailed'));
        return;
      }

      onSuccess();
    } catch (err) {
      setFormError(formatError(err) || t('paymentSaveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="billing-payment-form" onSubmit={handleSubmit}>
      <PaymentElement />
      {formError && <div className="error">{formError}</div>}
      <div className="billing-payment-form-actions">
        <button type="submit" className="btn btn-primary" disabled={!stripe || submitting}>
          {submitting ? t('saving') : t('saveCard')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          {t('cancel', { ns: 'common' })}
        </button>
      </div>
    </form>
  );
};

const Onboarding = () => {
  const { t } = useTranslation('onboarding');
  const { user, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightToken = searchParams.get('invitation');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [actionId, setActionId] = useState(null);

  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [companyForm, setCompanyForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    pricingPlan: DEFAULT_PLAN_ID
  });
  const [billingPlans, setBillingPlans] = useState(PRICING_PLANS);
  const [professionalTrialDays, setProfessionalTrialDays] = useState(PROFESSIONAL_TRIAL_DAYS);
  const [stripePromise, setStripePromise] = useState(null);
  const [billingConfigLoaded, setBillingConfigLoaded] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [locationForm, setLocationForm] = useState({ name: '', address: '' });
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'employee' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '' });
  // null = follow server currentStep; set only when browsing Back into history
  const [historyStepId, setHistoryStepId] = useState(null);

  const onboarding = user?.onboarding || { steps: [], currentStep: null, flow: null };
  const steps = onboarding.steps || [];
  const currentStepId = onboarding.currentStep || steps.find((step) => !step.complete)?.id;
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);
  const currentStep = steps[currentIndex] || null;
  const validHistoryStepId = historyStepId && steps.some((step) => step.id === historyStepId)
    ? historyStepId
    : null;
  const displayStepId = validHistoryStepId || currentStepId;
  const displayIndex = steps.findIndex((step) => step.id === displayStepId);
  const displayStep = steps[displayIndex] || null;
  const isViewingHistory = Boolean(
    validHistoryStepId && displayIndex >= 0 && currentIndex >= 0 && displayIndex < currentIndex
  );
  const companyId = getEffectiveCompanyId(user);

  const formatBillingPlanPrice = (plan) =>
    (plan.price === 0 ? t('freePlan') : t('paidPlanPrice', { price: plan.price }));

  const selectedPaidPlan = billingPlans.find((plan) => plan.id === companyForm.pricingPlan);
  const isProfessionalTrial =
    companyForm.pricingPlan === 'professional' && professionalTrialDays > 0;
  const isEnterprisePaid = companyForm.pricingPlan === 'enterprise';

  const getPaymentHelpCopy = () => {
    if (isProfessionalTrial) {
      return t('professionalTrialPaymentHelp', {
        days: professionalTrialDays,
        price: selectedPaidPlan?.price ?? 59
      });
    }
    if (isEnterprisePaid) {
      return t('enterprisePaymentHelp');
    }
    return t('paymentHelp');
  };

  const getPaidSubmitLabel = () => {
    if (isProfessionalTrial) {
      return t('startFreeTrial');
    }
    return t('payAndCreate');
  };

  const tourItems = useMemo(() => ([
    { key: 'dashboard', title: t('tourDashboardTitle'), description: t('tourDashboardDesc') },
    { key: 'tasks', title: t('tourTasksTitle'), description: t('tourTasksDesc') },
    { key: 'checkIn', title: t('tourCheckInTitle'), description: t('tourCheckInDesc') }
  ]), [t]);

  const requiredComplete = useMemo(
    () => steps.filter((step) => step.required).every((step) => step.complete),
    [steps]
  );

  useEffect(() => {
    setHistoryStepId(null);
  }, [currentStepId]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (displayStepId === 'invitation' && !isViewingHistory) {
      loadInvitations();
    }
  }, [displayStepId, isViewingHistory]);

  useEffect(() => {
    if (displayStepId !== 'company' || isViewingHistory || billingConfigLoaded) {
      return;
    }

    (async () => {
      try {
        const res = await axios.get('/api/billing/config');
        const configData = res.data.data;
        if (configData?.plans?.length) {
          setBillingPlans(configData.plans);
        }
        if (typeof configData?.professionalTrialDays === 'number') {
          setProfessionalTrialDays(configData.professionalTrialDays);
        }
        if (configData?.publishableKey) {
          setStripePromise(loadStripe(configData.publishableKey));
        }
      } catch (err) {
        // Fall back to local marketing catalog; paid checkout needs Stripe.
        console.warn('Billing config unavailable for onboarding:', err);
      } finally {
        setBillingConfigLoaded(true);
      }
    })();
  }, [displayStepId, isViewingHistory, billingConfigLoaded]);

  const clearPaymentState = useCallback(() => {
    setClientSecret(null);
    setStripeCustomerId(null);
    setPaymentReady(false);
  }, []);

  const setupIntentSeq = useRef(0);

  const startSetupIntent = useCallback(async (reuseCustomerId = null) => {
    const seq = ++setupIntentSeq.current;
    setPaymentLoading(true);
    setError('');
    setClientSecret(null);
    setPaymentReady(false);
    try {
      const body = reuseCustomerId ? { stripeCustomerId: reuseCustomerId } : {};
      const res = await axios.post('/api/billing/onboarding/setup-intent', body);
      if (seq !== setupIntentSeq.current) {
        return;
      }
      setClientSecret(res.data.data.clientSecret);
      setStripeCustomerId(res.data.data.stripeCustomerId);
      setPaymentReady(false);
    } catch (err) {
      if (seq !== setupIntentSeq.current) {
        return;
      }
      setError(formatError(err) || t('failedPaymentSetup'));
      clearPaymentState();
    } finally {
      if (seq === setupIntentSeq.current) {
        setPaymentLoading(false);
      }
    }
  }, [clearPaymentState, t]);

  const handlePlanChange = (planId) => {
    setCompanyForm((prev) => ({ ...prev, pricingPlan: planId }));
    if (planId === DEFAULT_PLAN_ID) {
      setupIntentSeq.current += 1;
      clearPaymentState();
      setPaymentLoading(false);
      return;
    }
    // Keep existing Stripe customer / SetupIntent when switching between paid tiers
    if (stripeCustomerId || paymentLoading || clientSecret) {
      return;
    }
    startSetupIntent();
  };

  const loadInvitations = async () => {
    try {
      const response = await axios.get('/api/companies/invitations');
      setInvitations(response.data.data || []);
    } catch (err) {
      setError(formatError(err) || t('failedLoadInvitations'));
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const patchOnboarding = async (payload) => {
    const response = await axios.patch('/api/auth/onboarding', payload);
    return response.data.data;
  };

  const handleSkip = async () => {
    if (!currentStep || currentStep.required) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      await patchOnboarding({ skipStep: currentStep.id });
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedSkip'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    setError('');
    try {
      await patchOnboarding({ finish: true });
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err) || t('failedFinish'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('email', user.email);
      payload.append('firstName', profileForm.firstName);
      payload.append('lastName', profileForm.lastName);
      payload.append('phone', profileForm.phone || '');
      await axios.put(`/api/users/${user.id}`, payload);
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedSaveProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (event) => {
    event.preventDefault();
    setError('');

    const isPaid = companyForm.pricingPlan !== DEFAULT_PLAN_ID;

    if (isPaid) {
      if (!stripePromise) {
        setError(t('paidPlanRequiresBilling'));
        return;
      }
      if (!stripeCustomerId || !clientSecret) {
        await startSetupIntent();
        return;
      }
      if (!paymentReady) {
        setError(t('saveCardBeforePaid'));
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        name: companyForm.name,
        address: companyForm.address || null,
        phone: companyForm.phone || null,
        email: companyForm.email || null,
        pricingPlan: companyForm.pricingPlan
      };
      if (isPaid) {
        payload.stripeCustomerId = stripeCustomerId;
      }
      await axios.post('/api/companies', payload);
      clearPaymentState();
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedCreateCompany'));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationCompanyId) => {
    setActionId(invitationCompanyId);
    setError('');
    try {
      await axios.post(`/api/companies/${invitationCompanyId}/join`);
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedAcceptInvitation'));
      setActionId(null);
    }
  };

  const handleLocationSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/locations', {
        name: locationForm.name,
        address: locationForm.address || null
      });
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedAddLocation'));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    if (!companyId) {
      setError(t('companyRequiredForInvite'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`/api/companies/${companyId}/invite`, {
        email: inviteForm.email,
        role: inviteForm.role
      });
      setInviteForm({ email: '', role: 'employee' });
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedSendInvitation'));
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/tasks', {
        title: taskForm.title,
        description: taskForm.description || null
      });
      setTaskForm({ title: '', description: '' });
      await refreshUser();
    } catch (err) {
      setError(formatError(err) || t('failedCreateTask'));
    } finally {
      setLoading(false);
    }
  };

  const handleTourComplete = async () => {
    setLoading(true);
    setError('');
    try {
      await patchOnboarding({ completeStep: 'tour' });
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err) || t('failedCompleteTour'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (displayIndex <= 0) {
      return;
    }
    setError('');
    setHistoryStepId(steps[displayIndex - 1].id);
  };

  const handleContinueFromHistory = () => {
    if (displayIndex < 0 || displayIndex >= steps.length - 1) {
      return;
    }
    setError('');
    const nextId = steps[displayIndex + 1].id;
    setHistoryStepId(nextId === currentStepId ? null : nextId);
  };

  const handleHistoryFormSubmit = (event) => {
    event.preventDefault();
    handleContinueFromHistory();
  };

  if (!user || !currentStep || !displayStep) {
    if (user && onboarding.isComplete) {
      navigate('/dashboard');
      return null;
    }
    return <LoadingSpinner className="onboarding-loading" />;
  }

  const progressPercent = steps.length
    ? Math.round(((currentIndex + 1) / steps.length) * 100)
    : 0;

  const backButton = displayIndex > 0 ? (
    <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={loading}>
      {t('back')}
    </button>
  ) : null;

  const progressLabel = t('progressLabel', { current: currentIndex + 1, total: steps.length });

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <header className="onboarding-header">
          <h1 className="onboarding-title">{t('welcomeTitle')}</h1>
          <p className="onboarding-subtitle">{t('welcomeSubtitle')}</p>
        </header>

        <nav className="onboarding-stepper" aria-label={progressLabel}>
          <div
            className="onboarding-progress-bar"
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={progressLabel}
          >
            <div className="onboarding-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <ol className="onboarding-steps-list">
            {steps.map((step, index) => (
              <li
                key={step.id}
                className={`onboarding-step-item${step.complete ? ' complete' : ''}${step.id === displayStepId ? ' current' : ''}`}
                aria-current={step.id === displayStepId ? 'step' : undefined}
              >
                <span className="onboarding-step-number" aria-hidden="true">
                  {step.complete ? '✓' : index + 1}
                </span>
                <span className="onboarding-step-label">
                  {t(`steps.${step.id}`, { defaultValue: step.label })}
                </span>
              </li>
            ))}
          </ol>
        </nav>

        <section
          className="onboarding-panel"
          aria-labelledby="onboarding-step-heading"
        >
          <h2 id="onboarding-step-heading" className="onboarding-panel-title">
            {t(`steps.${displayStep.id}`, { defaultValue: displayStep.label })}
          </h2>

          {error && <div className="onboarding-error" role="alert">{error}</div>}

          {displayStepId === 'profile' && (
            <form
              className="onboarding-form"
              onSubmit={isViewingHistory ? handleHistoryFormSubmit : handleProfileSubmit}
            >
              <p className="onboarding-help">{t('profileHelp')}</p>
              <div className="onboarding-form-group">
                <label htmlFor="firstName">{t('firstName')} *</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={profileForm.firstName}
                  onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                  required={!isViewingHistory}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-form-group">
                <label htmlFor="lastName">{t('lastName')} *</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={profileForm.lastName}
                  onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                  required={!isViewingHistory}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-form-group">
                <label htmlFor="phone">{t('phone')}</label>
                <input
                  id="phone"
                  name="phone"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-actions">
                {backButton}
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {isViewingHistory ? t('continue') : (loading ? t('saving') : t('saveAndContinue'))}
                </button>
              </div>
            </form>
          )}

          {displayStepId === 'company' && (
            isViewingHistory ? (
              <div className="onboarding-form">
                <p className="onboarding-help">{t('companyAlreadySetup')}</p>
                <div className="onboarding-actions">
                  {backButton}
                  <button type="button" className="btn btn-primary" onClick={handleContinueFromHistory}>
                    {t('continue')}
                  </button>
                </div>
              </div>
            ) : (
              <form className="onboarding-form onboarding-company-form" onSubmit={handleCompanySubmit}>
                <p className="onboarding-help">{t('companyHelp')}</p>
                <div className="form-group">
                  <label htmlFor="companyName">{t('companyName')} *</label>
                  <input
                    id="companyName"
                    name="name"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="companyEmail">{t('companyEmail')}</label>
                  <input
                    id="companyEmail"
                    type="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <span className="onboarding-plan-label" id="pricingPlanLabel">{t('pricingPlan')} *</span>
                  <div
                    className="billing-plans-grid onboarding-billing-plans"
                    role="radiogroup"
                    aria-labelledby="pricingPlanLabel"
                  >
                    {billingPlans.map((plan) => {
                      const selected = companyForm.pricingPlan === plan.id;
                      return (
                        <label
                          key={plan.id}
                          className={`billing-plan-card onboarding-billing-plan-card${selected ? ' current' : ''}`}
                        >
                          <input
                            type="radio"
                            name="pricingPlan"
                            value={plan.id}
                            checked={selected}
                            onChange={() => handlePlanChange(plan.id)}
                          />
                          <div className="billing-plan-header">
                            <h3>{plan.name}</h3>
                            <div className="billing-plan-price">{formatBillingPlanPrice(plan)}</div>
                            {plan.id === 'professional' && professionalTrialDays > 0 && (
                              <p className="billing-plan-trial-note">
                                {t('professionalTrialBadge', { days: professionalTrialDays })}
                              </p>
                            )}
                          </div>
                          {plan.description && (
                            <p className="billing-plan-description">{plan.description}</p>
                          )}
                          {Array.isArray(plan.features) && plan.features.length > 0 && (
                            <ul className="billing-plan-features">
                              {plan.features.map((feature) => (
                                <li key={feature}>{feature}</li>
                              ))}
                            </ul>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {companyForm.pricingPlan !== DEFAULT_PLAN_ID && (
                  <div className="onboarding-payment-section">
                    <h3 className="onboarding-payment-title">{t('paymentDetails')}</h3>
                    <p className="onboarding-help">
                      {getPaymentHelpCopy()}
                    </p>
                    {paymentLoading && <LoadingSpinner />}
                    {paymentReady && (
                      <p className="onboarding-payment-ready">{t('cardSaved')}</p>
                    )}
                    {!paymentReady && clientSecret && stripePromise && (
                      <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <OnboardingPaymentForm
                          onSuccess={() => setPaymentReady(true)}
                          onCancel={() => {
                            clearPaymentState();
                            setCompanyForm((prev) => ({ ...prev, pricingPlan: DEFAULT_PLAN_ID }));
                          }}
                        />
                      </Elements>
                    )}
                    {!paymentLoading && !clientSecret && !paymentReady && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={startSetupIntent}
                        disabled={!stripePromise}
                      >
                        {t('addPaymentMethod')}
                      </button>
                    )}
                  </div>
                )}

                <div className="onboarding-actions">
                  {backButton}
                  <button type="submit" className="btn btn-primary" disabled={loading || paymentLoading}>
                    {loading
                      ? t('creating')
                      : companyForm.pricingPlan === DEFAULT_PLAN_ID
                        ? t('createCompany')
                        : paymentReady
                          ? getPaidSubmitLabel()
                          : t('continue')}
                  </button>
                </div>
              </form>
            )
          )}

          {displayStepId === 'invitation' && (
            <div className="onboarding-invitations">
              {isViewingHistory ? (
                <>
                  <p className="onboarding-help">{t('alreadyJoined')}</p>
                  <div className="onboarding-actions">
                    {backButton}
                    <button type="button" className="btn btn-primary" onClick={handleContinueFromHistory}>
                      {t('continue')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="onboarding-help">{t('invitationHelp')}</p>
                  {invitations.length === 0 ? (
                    <EmptyState
                      icon="mail"
                      title={t('noPendingInvitations')}
                      description={t('noPendingInvitationsDesc')}
                    />
                  ) : (
                    <ul className="onboarding-invitation-list">
                      {invitations.map((invitation) => {
                        const isHighlighted =
                          highlightToken
                          && (invitation.invitation_token === highlightToken || invitation.token === highlightToken);
                        return (
                          <li
                            key={invitation.id}
                            className={`onboarding-invitation-card${isHighlighted ? ' highlighted' : ''}`}
                          >
                            <div>
                              <strong>{invitation.name}</strong>
                              {invitation.invited_by_name && (
                                <p>{t('invitedBy', { name: invitation.invited_by_name })}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleAcceptInvitation(invitation.id)}
                              disabled={actionId === invitation.id}
                            >
                              {actionId === invitation.id ? t('accepting') : t('accept')}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {displayIndex > 0 && (
                    <div className="onboarding-actions">
                      {backButton}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {displayStepId === 'location' && (
            <form
              className="onboarding-form"
              onSubmit={isViewingHistory ? handleHistoryFormSubmit : handleLocationSubmit}
            >
              <p className="onboarding-help">{t('locationHelp')}</p>
              <div className="onboarding-form-group">
                <label htmlFor="locationName">{t('locationName')} *</label>
                <input
                  id="locationName"
                  value={locationForm.name}
                  onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                  required={!isViewingHistory}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-form-group">
                <label htmlFor="locationAddress">{t('address')}</label>
                <input
                  id="locationAddress"
                  value={locationForm.address}
                  onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-actions">
                {backButton}
                {isViewingHistory ? (
                  <button type="submit" className="btn btn-primary">{t('continue')}</button>
                ) : (
                  <>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? t('saving') : t('addLocation')}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleSkip} disabled={loading}>
                      {t('skipForNow')}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}

          {displayStepId === 'invite' && (
            <form
              className="onboarding-form"
              onSubmit={isViewingHistory ? handleHistoryFormSubmit : handleInviteSubmit}
            >
              <p className="onboarding-help">{t('inviteHelp')}</p>
              <div className="onboarding-form-group">
                <label htmlFor="inviteEmail">{t('inviteEmail')} *</label>
                <input
                  id="inviteEmail"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  required={!isViewingHistory}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-form-group">
                <label htmlFor="inviteRole">{t('role')}</label>
                <select
                  id="inviteRole"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  disabled={isViewingHistory}
                >
                  <option value="employee">{t('roleEmployee')}</option>
                  <option value="admin">{t('roleAdmin')}</option>
                  <option value="reader">{t('roleReader')}</option>
                </select>
              </div>
              <div className="onboarding-actions">
                {backButton}
                {isViewingHistory ? (
                  <button type="submit" className="btn btn-primary">{t('continue')}</button>
                ) : (
                  <>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? t('sending') : t('sendInvitation')}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleSkip} disabled={loading}>
                      {t('skipForNow')}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}

          {displayStepId === 'task' && (
            <form
              className="onboarding-form"
              onSubmit={isViewingHistory ? handleHistoryFormSubmit : handleTaskSubmit}
            >
              <p className="onboarding-help">{t('taskHelp')}</p>
              <div className="onboarding-form-group">
                <label htmlFor="taskTitle">{t('taskTitle')} *</label>
                <input
                  id="taskTitle"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  required={!isViewingHistory}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-form-group">
                <label htmlFor="taskDescription">{t('description')}</label>
                <textarea
                  id="taskDescription"
                  rows={3}
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  readOnly={isViewingHistory}
                />
              </div>
              <div className="onboarding-actions">
                {backButton}
                {isViewingHistory ? (
                  <button type="submit" className="btn btn-primary">{t('continue')}</button>
                ) : (
                  <>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? t('creating') : t('createTask')}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleSkip} disabled={loading}>
                      {t('skipForNow')}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}

          {displayStepId === 'tour' && (
            <div className="onboarding-tour">
              <p className="onboarding-help">{t('tourHelp')}</p>
              <ul className="onboarding-tour-list">
                {tourItems.map((item) => (
                  <li key={item.key} className="onboarding-tour-item">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </li>
                ))}
              </ul>
              <div className="onboarding-actions">
                {backButton}
                <button type="button" className="btn btn-primary" onClick={handleTourComplete} disabled={loading}>
                  {loading ? t('finishing') : t('goToDashboard')}
                </button>
              </div>
            </div>
          )}
        </section>

        {requiredComplete && displayStepId !== 'tour' && (
          <footer className="onboarding-footer">
            <button
              type="button"
              className="btn btn-primary onboarding-finish-btn"
              onClick={handleFinish}
              disabled={loading}
            >
              {t('finishSetup')}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
